import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Users,
  Shield,
  ShieldCheck,
  CalendarClock,
  CalendarDays,
  Megaphone,
  Building2,
  GraduationCap,
  ToggleLeft,
  Layers,
  BarChart3,
  Activity,
  UserCog,
  BookOpen,
  Clapperboard,
  Newspaper,
  Rocket,
} from "lucide-react";

import { hasPermission, type Permission } from "~/lib/shared/permissions";

export const Route = createFileRoute("/admin/")({
  staticData: { title: "Admin Dashboard" },
  component: AdminDashboard,
});

interface AdminTool {
  to: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Opens the tool's page; the card shows only to people who hold it. */
  permission: Permission;
}

const tools: AdminTool[] = [
  {
    to: "/admin/cih/ccos",
    permission: "cih:read",
    label: "CCOs",
    description:
      "Manage Coach Certifying Organizations — accreditation bodies and training orgs.",
    icon: Building2,
  },
  {
    to: "/admin/ctps",
    permission: "cih:read",
    label: "CTPs",
    description:
      "Manage Coaching Training Programs and their parent organizations.",
    icon: GraduationCap,
  },
  {
    to: "/admin/verifications",
    permission: "coaches:read",
    label: "Verifications",
    description: "Verify coach CCO memberships and CTP certifications.",
    icon: ShieldCheck,
  },
  {
    to: "/admin/coaches",
    permission: "coaches:read",
    label: "Coach Directory",
    description:
      "Find coaches by approval, billing, Nova use, or activity; act on them and export lists.",
    icon: Users,
  },
  {
    to: "/admin/fee-exemptions",
    permission: "coaches:read",
    label: "Fee Exemptions",
    description: "Manage platform fee exemptions for coaches.",
    icon: Shield,
  },
  {
    to: "/admin/summit-cta",
    permission: "summit:read",
    label: "Summit CTA",
    description:
      "Control the live call-to-action button during summit streams.",
    icon: Megaphone,
  },
  {
    to: "/admin/summit-sessions",
    permission: "summit:read",
    label: "Summit Sessions",
    description: "View session registrations and registrant details.",
    icon: CalendarDays,
  },
  {
    to: "/admin/users",
    permission: "users:read",
    label: "Users",
    description:
      "Search accounts, schedule or cancel deletion, or delete immediately.",
    icon: UserCog,
  },
  {
    to: "/admin/calls-and-links",
    permission: "calls-and-links:read",
    label: "Calls and links",
    description:
      "Set the office hours and Accelerator call schedules, their join links, and the Book-a-call link.",
    icon: CalendarClock,
  },
  {
    to: "/admin/feature-flags",
    permission: "feature-flags:read",
    label: "Feature Flags",
    description: "Manage feature flag rollout and per-user access.",
    icon: ToggleLeft,
  },
  {
    to: "/admin/nova-usage",
    permission: "nova-usage:read",
    label: "Nova Usage",
    description: "Monitor Nova token usage and model cost by coach account.",
    icon: BarChart3,
  },
  {
    to: "/admin/funnel-health",
    permission: "funnel-health:read",
    label: "Funnel Health",
    description: "Inspect event delivery backlog and measured coach activity.",
    icon: Activity,
  },
  {
    to: "/admin/whats-new",
    permission: "whats-new:read",
    label: "What's new",
    description: "Write and publish the updates coaches read.",
    icon: Newspaper,
  },
  {
    to: "/admin/features",
    permission: "features:read",
    label: "Features",
    description:
      "Add and edit coach-facing features, move them forward and see how many coaches want each one.",
    icon: Rocket,
  },
  {
    to: "/admin/cih",
    permission: "cih:read",
    label: "CIH Content",
    description:
      "Manage challenges, frameworks, coach articles, and success stories.",
    icon: Layers,
  },
  {
    to: "/admin/courses",
    permission: "courses:read",
    label: "Courses",
    description: "Review and edit course weeks, class videos and materials.",
    icon: BookOpen,
  },
  {
    to: "/admin/videos",
    permission: "videos:read",
    label: "Video library",
    description: "See every video, where it is shown, and preview it.",
    icon: Clapperboard,
  },
];

function AdminDashboard(): React.ReactElement {
  const { adminPermissions } = Route.useRouteContext();
  const visibleTools = tools.filter((tool) =>
    hasPermission(adminPermissions, tool.permission),
  );
  return (
    <div>
      <h1 className="mb-2 text-2xl font-medium text-gray-900">
        Admin Dashboard
      </h1>
      <p className="mb-8 text-sm text-gray-500">
        Dream Coach Match administration tools.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleTools.map((tool) => (
          <Link
            key={tool.to}
            to={tool.to}
            className="group rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-gray-300 hover:shadow-md"
          >
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition group-hover:bg-brand-50 group-hover:text-brand-600">
                <tool.icon className="h-5 w-5" />
              </div>
              <h2 className="font-medium text-gray-900">{tool.label}</h2>
            </div>
            <p className="text-sm leading-relaxed text-gray-500">
              {tool.description}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}
